import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import Icon from '../../../components/AppIcon';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg min-w-[160px]">
      <p className="text-xs font-semibold text-foreground mb-2">{label}</p>
      {payload?.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: entry?.color }} />
            <span className="text-xs text-muted-foreground">{entry?.name}</span>
          </div>
          <span className="text-xs font-semibold text-foreground">${parseFloat(entry?.value || 0)?.toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
};

const RevenueTrendsChart = ({ data, loading, totalEarned, monthlyGrowth }) => {
  const chartData = useMemo(() => {
    if (!data?.length) return [];
    return data?.map(d => ({
      month: d?.month,
      earnings: parseFloat(d?.earnings || 0),
      credits: parseFloat(d?.credits || 0),
    }));
  }, [data]);

  const avgEarnings = chartData?.length
    ? chartData?.reduce((s, d) => s + d?.earnings, 0) / chartData?.length
    : 0;

  const isPositiveGrowth = (monthlyGrowth || 0) >= 0;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #14B8A6, #3B82F6)' }}>
              <Icon name="TrendingUp" size={18} color="#fff" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground font-heading">Revenue Trends</h3>
              <p className="text-xs text-muted-foreground">Monthly earnings from wallet ledger</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-foreground data-text">${parseFloat(totalEarned || 0)?.toFixed(2)}</p>
            <div className={`flex items-center justify-end gap-1 text-xs font-medium ${
              isPositiveGrowth ? 'text-emerald-400' : 'text-red-400'
            }`}>
              <Icon name={isPositiveGrowth ? 'TrendingUp' : 'TrendingDown'} size={12} />
              {isPositiveGrowth ? '+' : ''}{(monthlyGrowth || 0)?.toFixed(1)}% vs last month
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="p-5">
        {loading ? (
          <div className="h-52 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-muted-foreground">Loading chart data...</p>
            </div>
          </div>
        ) : chartData?.length === 0 ? (
          <div className="h-52 flex items-center justify-center">
            <div className="text-center">
              <Icon name="BarChart2" size={32} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No earnings data yet</p>
              <p className="text-xs text-muted-foreground mt-1">Complete campaigns to see trends</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={210}>
            <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="earningsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#14B8A6" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#14B8A6" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="creditsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.5)" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fill: '#94A3B8', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#94A3B8', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={v => `$${v}`}
              />
              <Tooltip content={<CustomTooltip />} />
              {avgEarnings > 0 && (
                <ReferenceLine
                  y={avgEarnings}
                  stroke="#F59E0B"
                  strokeDasharray="4 4"
                  strokeOpacity={0.5}
                  label={{ value: 'Avg', fill: '#F59E0B', fontSize: 10, position: 'right' }}
                />
              )}
              <Area
                type="monotone"
                dataKey="credits"
                name="Credits"
                stroke="#F59E0B"
                strokeWidth={1.5}
                fill="url(#creditsGrad)"
                dot={false}
                activeDot={{ r: 4, fill: '#F59E0B' }}
              />
              <Area
                type="monotone"
                dataKey="earnings"
                name="Earnings"
                stroke="#14B8A6"
                strokeWidth={2}
                fill="url(#earningsGrad)"
                dot={false}
                activeDot={{ r: 5, fill: '#14B8A6' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Legend */}
      <div className="px-5 pb-4 flex items-center gap-5">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 bg-primary rounded" />
          <span className="text-xs text-muted-foreground">Earnings</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 bg-amber-400 rounded" />
          <span className="text-xs text-muted-foreground">Credits</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-0.5 border-t border-dashed border-amber-400" />
          <span className="text-xs text-muted-foreground">Average</span>
        </div>
      </div>
    </div>
  );
};

export default RevenueTrendsChart;
